import { partition } from "es-toolkit";
import { computed, makeObservable, reaction } from "mobx";

import { BaseNodeModel } from "@/models/nodes/BaseNodeModel";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { MatrixNodeModel } from "@/models/nodes/MatrixNodeModel";
import { isNodeHighlighted } from "@/models/nodes/utils";
import { isSupSubNode } from "@/models/utils/semanticChecks";

import { DecoratorNodeModel } from "./DecoratorNodeModel";
import { closingToOpeningMap, openingToClosingMap } from "./groups/utils";
import { OperatorSymbolNodeModel } from "./OperatorSymbolNodeModel";
import { VariableNodeModel } from "./VariableNodeModel";

import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel, NodeId } from "@/models/nodes/types";
import type {
  VisitableNode,
  TransformableNode,
  NodeVisitor,
} from "@/models/nodes/visitors/NodeVisitor";
import type {
  ClosingSymbol,
  GroupNodeProps,
  GroupNodeConstructorProps,
  OpeningSymbol,
  MatrixNodeProps,
  MatrixBounds,
} from "@corca/data";

type GroupNodeModelCreateProps = Partial<MatrixNodeProps> & {
  casesSystemEnabled?: boolean;
  omitParentheses?: boolean;
};

export class GroupNodeModel
  extends BaseNodeModel<GroupNodeProps>
  implements VisitableNode, TransformableNode
{
  static type = "group" as const;

  type = GroupNodeModel.type;

  get isNested() {
    return true;
  }

  needsTransform: boolean = false;

  protected fillRequiredProps(props: GroupNodeConstructorProps): GroupNodeProps {
    return props;
  }

  static create(
    doc: DocumentModel,
    nodes: NodeModel[] = [],
    openingSymbol?: OpeningSymbol,
    closingSymbol?: ClosingSymbol,
    {
      rows = 1,
      cols = 1,
      casesSystemEnabled,
      omitParentheses,
      ...matrixProps
    }: GroupNodeModelCreateProps = {},
    id?: NodeId,
  ) {
    const [decorators, groupNodes] = partition(nodes, (n) => n.is(DecoratorNodeModel));
    const matrixNodes =
      rows * cols !== groupNodes.length || !groupNodes.every((n) => n.is(InlineNodeModel))
        ? [doc.create(InlineNodeModel, groupNodes)]
        : groupNodes;

    const groupProps = {
      openingSymbol,
      closingSymbol,
      casesSystemEnabled,
      omitParentheses,
    };

    return new GroupNodeModel(doc, id, groupProps, [
      doc.create(
        MatrixNodeModel,
        groupNodes.length === 1 ? { rows: 1, cols: 1 } : { rows, cols, ...matrixProps },
        matrixNodes,
      ),
      ...decorators,
    ]);
  }

  constructor(...args: ConstructorParameters<typeof BaseNodeModel<GroupNodeConstructorProps>>) {
    super(...args);
    makeObservable(this, {
      inlineNode: true,
      matrixNode: true,
      isExpandable: true,
      openingSymbol: true,
      closingSymbol: true,
      needsTransform: false,
      omitParentheses: true,
      isHighlighted: computed,
    });

    this.onCleanup(
      reaction(
        () => this.matrixNode?.bounds,
        (bounds) => this.onBoundsChange(bounds),
      ),
    );
  }

  prepareUpdate(skipTransform?: boolean): void {
    super.prepareUpdate(skipTransform);

    if (!skipTransform) {
      this.doc.nodesToTransform.add(this.id);
    }
  }

  transform() {
    if (!this.needsTransform) return;
    this.needsTransform = false;

    const siblingIndex = isSupSubNode(this.prevSiblings[0]) ? 1 : 0;
    const prevSibling = this.prevSiblings[siblingIndex];

    if (!prevSibling?.is(VariableNodeModel) || prevSibling.hasParams) {
      return;
    }

    if (this.matrixNode?.rows === 1 && this.matrixNode?.cols === 1) {
      const forceConvert = [...this.doc.nodes.values()].some((n) => {
        if (!n.is(VariableNodeModel) || n.value !== prevSibling.value) return false;
        return n.hasParams;
      });
      const inlineNode = this.matrixNode.matrixNodes[0];
      const hasExpressions = inlineNode.nodes.some((n) => {
        if (n.is(OperatorSymbolNodeModel)) {
          const [prev] = n.prevSiblings;
          const [next] = n.nextSiblings;

          if (!prev || !next) {
            return false;
          }

          return !prev.is(OperatorSymbolNodeModel) && !next.is(OperatorSymbolNodeModel);
        }

        return false;
      });

      if (forceConvert || !hasExpressions) {
        this.doc.editor.stopUndoCapturing();
        this.doc.transact(() => {
          prevSibling.appendChild(inlineNode);
          if (this.openingSymbol || this.closingSymbol) {
            prevSibling.openingSymbol =
              this.openingSymbol ?? (this.closingSymbol && closingToOpeningMap[this.closingSymbol]);
            prevSibling.closingSymbol =
              this.closingSymbol ?? (this.openingSymbol && openingToClosingMap[this.openingSymbol]);
          }
          this.doc.removeNode(this);
        }, "GroupNodeModel:transform");
      }
    }
  }

  onBoundsChange(bounds?: MatrixBounds) {
    if (!bounds) return;

    if (this.omitParentheses && (bounds.rows > 1 || bounds.cols > 1)) {
      this.omitParentheses = false;
    }
  }

  get inlineNode(): InlineNodeModel | undefined {
    const { rows, cols } = this.matrixNode || {};

    if (rows === 1 && cols === 1) {
      return this.matrixNode?.getCellNodeByPosition({ col: 0, row: 0 });
    }

    return undefined;
  }

  get matrixNode() {
    return this.children.find((n) => n.is(MatrixNodeModel));
  }

  get decorators() {
    return this.children.filter((n) => n.is(DecoratorNodeModel));
  }

  get isExpandable() {
    if (this.casesSystemEnabled || this.omitParentheses) {
      return false;
    }

    const { rows, cols } = this.matrixNode || {};
    return rows === 1 && cols === 1;
  }

  get openingSymbol() {
    if (this.omitParentheses) {
      return undefined;
    }

    return this.props.openingSymbol;
  }

  set openingSymbol(openingSymbol: OpeningSymbol | undefined) {
    this.updateProps({ openingSymbol });

    if (openingSymbol && this.omitParentheses) {
      this.omitParentheses = false;

      if (this.closingSymbol) {
        this.closingSymbol = undefined;
      }
    }
  }

  get closingSymbol() {
    if (this.casesSystemEnabled || this.omitParentheses) {
      return undefined;
    }

    return this.props.closingSymbol;
  }

  set closingSymbol(closingSymbol: ClosingSymbol | undefined) {
    this.updateProps({ closingSymbol });

    if (closingSymbol && this.omitParentheses) {
      this.omitParentheses = false;

      if (this.openingSymbol) {
        this.openingSymbol = undefined;
      }
    }
  }

  get casesSystemEnabled() {
    return this.props.casesSystemEnabled;
  }

  set casesSystemEnabled(casesSystemEnabled: boolean | undefined) {
    this.updateProps({ casesSystemEnabled });
  }

  get omitParentheses() {
    return !!this.props.omitParentheses || this.hasWrappingDecorators;
  }

  set omitParentheses(omitParentheses: boolean) {
    this.updateProps({ omitParentheses });

    if (!omitParentheses && !this.openingSymbol && !this.closingSymbol) {
      this.openingSymbol = "(";
      this.closingSymbol = ")";
    }
  }

  get isHighlighted(): boolean {
    return isNodeHighlighted(this.doc, this);
  }

  get hasWrappingDecorators() {
    return this.decorators.some((decorator) => decorator.name === "boxed");
  }

  accept<R>(visitor: NodeVisitor<R>): R {
    return visitor.visitGroupNode(this);
  }
}
